import { HttpClient, HttpClientRequest, HttpClientResponse } from '@effect/platform';
import { Address, ProtocolKey, SiloData, makeAddressSync } from '@silo/types';
import { useSiloWeb3 } from '@silo/web3';
import { QueryFunction, useQuery } from '@tanstack/react-query';
import { Effect, pipe } from 'effect';
import { durationToMilliseconds } from '@silo/utils';

export const siloDataKey = 'siloData';

type SiloDataKey = [
  key: typeof siloDataKey,
  protocolKey: ProtocolKey,
  marketAddress: Address,
  user: Address | null
];

interface UseQuerySiloDataParams {
  marketAddress: Address | null | undefined;
  enabled?: boolean;
}

export function useQuerySiloData({ marketAddress, enabled = true }: UseQuerySiloDataParams) {
  const { protocolInstance, account } = useSiloWeb3();

  const user = account ? makeAddressSync(account) : null;

  const queryKey: SiloDataKey = [siloDataKey, protocolInstance!, marketAddress!, user];

  return useQuery({
    queryKey,
    queryFn: queryFn,
    enabled: enabled && !!protocolInstance && !!marketAddress,
    staleTime: durationToMilliseconds({ minutes: 1 }),
    keepPreviousData: true,
  });
}

const queryFn: QueryFunction<SiloData, SiloDataKey> = async ({
  queryKey: [, protocolKey, marketAddress, user],
}) => {
  const result = await pipe(
    HttpClientRequest.get('/api/silo'),
    HttpClientRequest.appendUrlParams({
      protocol: protocolKey,
      market: marketAddress,
      ...(user ? { user } : {}),
    }),
    HttpClient.fetchOk,
    Effect.andThen(HttpClientResponse.schemaBodyJson(SiloData)),
    Effect.scoped,
    Effect.runPromise
  );

  return result;
};
